import {css, StyleSheet} from 'aphrodite';
import Backbone from 'backbone';
import $ from 'jquery';

import * as analytics from 'analytics/Analytics';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {CleanMetadataResponse, Sequence, SequenceVersion, Versions} from 'components/Sequence';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorView, {FolderSelectorViewOptions} from 'file-browser/FolderSelectorView';
import FileStates, {EditVersionState, HistoryPanelState} from 'FileStates';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import Enzymes from 'molbio/enzymes/backbone/Enzymes';
import notifications from 'notifications';
import * as popup from 'Popup';
import {renderSinglePanelTab} from 'toolbar/shared/PanelUtil';
import VersionTemplate from 'toolbar/VersionTemplate';
import * as DateUtil from 'util/DateUtil';
import {ElementMouseEvent, InputChangeEvent} from 'util/Types';
import * as VersionHistoryUtil from 'util/VersionHistoryUtil';

class EditVersion extends Backbone.View<EditVersionState> {
  editState!: EditVersionState;
  sequence!: Sequence;
  selectorView: FolderSelectorView | undefined;
  version: SequenceVersion | undefined;

  static initClass(): void {
    this.prototype.el = '#edit-version';

    this.prototype.events = {
      // @ts-expect-error
      'change .clone-name': 'changeCloneName',
      'click .clone': 'cloneVersion',
      'click .reset': 'resetToVersion',
    };
  }

  changeCloneName(): void {
    this.editState.set('cloneName', this.$el.find('.clone-name').val());
  }

  close(): void {
    this.$el.addClass('hide');
    if (this.editState != null) {
      this.editState.clear().set(this.editState.defaults());
    }
  }

  cloneVersion(): void {
    const cloneName = this.editState.get('cloneName');
    if (!cloneName) {
      notifications.error('Please enter a valid name.');
      return;
    }

    const folderId = this.$('.clone-folder').val();
    if (!folderId) {
      notifications.error('Please select a folder.');
      return;
    }

    this.sequence.duplicate({
      name: this.editState.get('cloneName'),
      version: this.editState.get('version').get('id'),
      folderId,
      skipPrompt: true,
    });
    analytics.track('editor', 'cloned_sequence_version');
  }

  resetToVersion(): void {
    if (this.sequence.get('currentVersion')) {
      notifications.error('You cannot reset to the current version.');
    } else {
      const version = this.editState.get('version');
      const {sequence} = this;
      version
        .cleanMetadata()
        .fail(notifications.notifyError)
        .done((results: CleanMetadataResponse) => {
          if (results.all_valid) {
            sequence.resetToVersion(version);
            sequence.selection().clampToSequence();
            return;
          }
          // Assuming for now that primers are the only things wrong.
          const msg = $('<div>').html(
            '<p>' +
              'The selected version has primers attached that no longer exist in your folders. ' +
              'Resetting will restore the version with these primers omitted:' +
              '</p>' +
              '<ul class="removed-primers list-group"></ul>'
          );
          for (const primer of results.invalid_primers) {
            const $item = $('<li class="list-group-item">').text(primer.name);
            msg.find('.removed-primers').append($item);
          }
          new popup.Confirm({title: 'Reset Warning', msg})
            .choice('OK', 'btn-af-primary', () => sequence.resetToVersion(version))
            .choice('Cancel', 'btn-af-default')
            .show();
        });
    }
  }

  renderFolderSelector(selectedFolderId?: string): void {
    const initialFolderId = selectedFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY);

    if (this.selectorView != null) {
      this.selectorView.remove();
    }
    const folderSelectorViewOptions: FolderSelectorViewOptions = {
      initialFolderId,
      inputClassName: 'clone-folder',
      props: {showInventory: true, readOnly: !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE})},
    };
    this.selectorView = new FolderSelectorView(folderSelectorViewOptions);
    this.$('.folder-selector-container').html(this.selectorView.render().el);
  }

  update(editState: EditVersionState, sequence: Sequence): void {
    this.editState = editState;
    this.sequence = sequence;
    if (this.version != null) {
      this.stopListening(this.version);
    }

    this.version = this.editState.get('version');
    if (this.version) {
      this.$el.find('.clone-name').val(this.editState.get('cloneName'));
      this.$el.removeClass('hide');
    } else {
      this.$el.addClass('hide');
    }

    this.$('.reset').prop(
      'disabled',
      this.sequence.get('currentVersion') || !this.sequence.allowResidueEdits({ignoreVersion: true})
    );
    this.$('.clone').prop('disabled', !isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}));
    this.renderFolderSelector();
  }
}

EditVersion.initClass();

class SequenceHistoryPanelView extends Backbone.View<HistoryPanelState> {
  panelState!: HistoryPanelState;
  showAllVersions!: boolean;
  edit!: EditVersion;
  enzymes!: Enzymes;
  sequences!: FileStates;
  sequence!: Sequence;
  versions: Versions | undefined;
  editState: EditVersionState | undefined;

  static initClass(): void {
    this.prototype.el = '#history-panel';

    this.prototype.events = {
      // @ts-expect-error
      'click .js-history-subpanel .version-row': 'clickVersionRow',
      'click .js-fragment-pair': 'openPair',
      'click .js-fragment-primer': 'openPrimer',
      'click .js-fragment-sequence-name': 'openFragmentSequence',
      'click .show-all-versions': 'toggleShowAllVersions',
    };
  }

  override initialize(): void {
    this.showAllVersions = false;
    this.edit = new EditVersion();
    this.enzymes = Enzymes.getCommonEnzymes();
    this.sequences = FileStates.get();
  }

  _versionsMetadata(): Array<VersionHistoryUtil.VersionInfo> {
    return this.versions!.sortBy((a) => a.get('id')).map((v) => ({
      id: v.get('id'),
      parentId: v.get('parentId'),
    }));
  }

  clickVersionRow(e: ElementMouseEvent): void {
    const node = $(e.currentTarget);
    this.openVersion(this.versions!.get(node.attr('version-id')));
  }

  openVersion(version: SequenceVersion): void {
    if (this.sequence.getActiveVersionId() === version.id) {
      this.showEdit(version);
    } else {
      analytics.track('editor', 'changed_sequence_version');
      this.sequence.viewVersion(version).done(() => {
        this.showEdit(version);
      });
    }
  }

  showEdit(version: SequenceVersion): void {
    this.editState!.set({
      version,
      cloneName: `${this.sequence.get('_sequence').get('name')} - ${version.get('name')}`,
    });
    this.edit.update(this.editState!, this.sequence);
  }

  isVersionHistoryTruncated(): boolean {
    return (
      Boolean(this.versions?.versionsWereTruncated) || this.sequence.get('_sequence').versionsWereTruncated()
    );
  }

  renderRows(): void {
    const rows = this.$el.find('#version-rows');
    rows.empty();

    this.$el.find('.js-versions-truncated-warning').toggleClass('hide', !this.isVersionHistoryTruncated());

    this.$el
      .find('.show-all-versions')
      .toggleClass('hidden', !VersionHistoryUtil.hasReverts(this._versionsMetadata()));

    const activeVersion = this.sequence.getActiveVersion();
    const revertedInfo = VersionHistoryUtil.processVersions(this._versionsMetadata());
    for (const version of this.versions!.sortBy((a) => a.get('id')).reverse()) {
      let time;
      const d = new Date(version.get('created_at'));
      const user = version.get('user')?.name ?? '';
      if (d.getFullYear() === new Date().getFullYear()) {
        time = DateUtil.readableTime(d, {showYear: false, showTime: true, showSeconds: true});
      } else {
        time = DateUtil.readableTime(d, {showYear: true, showTime: true, showSeconds: true});
      }

      const isActive = activeVersion === version;
      const isReverted = revertedInfo[version.get('id')];
      if (!isReverted || this.showAllVersions) {
        // oxlint-disable-next-line new-cap
        const row = VersionTemplate({
          id: version.get('id'),
          name: version.get('name'),
          time,
          active: isActive,
          user,
          reverted: isReverted,
        });
        this.$el.find('#version-rows').append(row);
      }

      if (isActive) {
        this.$el.find(`[version-id=${version.get('id')}]`).click();
      }
    }
  }

  selectRow(versionId: number): void {
    this.$('.js-history-subpanel .version-row').removeClass(css(styles.selectedVersionRow));
    this.$(`.js-history-subpanel .version-row[version-id=${versionId}]`).addClass(
      css(styles.selectedVersionRow)
    );
  }

  openPair(e: ElementMouseEvent): void {
    const $a = $(e.currentTarget);
    const pairId = $a.data('pairId');
    this.sequence.selectPrimerPair(pairId);
  }

  openPrimer(e: ElementMouseEvent): void {
    const $a = $(e.currentTarget);
    const primerId = $a.data('primerId');
    const primer = this.sequence.sequencePrimers().get(primerId);
    primer.dblClick(this.sequence, e.shiftKey);
  }

  openFragmentSequence(e: ElementMouseEvent): void {
    const fragmentSequenceID = $(e.currentTarget).data('fragment-sequence-id');
    FileBrowserActions.openFile(fragmentSequenceID);
  }

  toggleShowAllVersions(e: InputChangeEvent): void {
    this.showAllVersions = $(e.target).is(':checked');
    this.renderRows();
  }

  update(panelState: HistoryPanelState, sequence: Sequence): void {
    renderSinglePanelTab(
      'sequence-version-history-tab',
      'sequence-version-history-panel',
      'Version history',
      this.$('.panel-title')[0]
    );
    if (this.panelState != null) {
      this.stopListening(this.panelState);
    }
    if (this.sequence != null) {
      this.stopListening(this.sequence);
    }
    this.panelState = panelState;
    this.sequence = sequence;
    if (this.versions != null) {
      this.stopListening(this.versions);
    }
    this.versions = sequence.get('versions');

    if (this.editState != null) {
      this.stopListening(this.editState);
    }
    this.editState = this.panelState.get('editState');

    if (this.sequence.get('writable')) {
      this.edit.update(this.editState!, this.sequence);
    } else {
      this.edit.$el.addClass('hide');
    }

    this.renderRows();
    this.listenTo(this.versions, 'reloaded change:name', () => {
      this.renderRows();
    });
    this.listenTo(this.sequence, 'change:activeVersionId', (_: Sequence, versionId: number) => {
      this.selectRow(versionId);
    });
    this.listenTo(this.versions, 'open', (version: SequenceVersion) => {
      this.openVersion(version);
    });
  }
}

const styles = StyleSheet.create({
  selectedVersionRow: {
    color: '#000DB5',
    backgroundColor: '#E8EDFF',
    fontWeight: 600,
  },
});

SequenceHistoryPanelView.initClass();

export default SequenceHistoryPanelView;
